/**
 * @typedef {{
*  "sizes": "SIZES";
*  "collection": "COLLECTION";
*  "hotspot": "HOTSPOT";
*  "search": "SEARCH";
*  "search-b2b-list": "SEARCH-B2B-LIST";
*  "details": "DETAILS";
*  "details-b2b": "details-B2B";
* }} OmnibusType
*/

/**
* @typedef {{
*  currency: string;
*  value: number;
*  formatted: string;
* }} OmnibusPrice
*/

/**
* @typedef {{
*  maxprice?: string | undefined;
*  sizeMin:? string | undefined;
*  sizeMax:? string | undefined;
*  sizeMinMaxprice?: string | undefined;
*  sizeMaxMaxprice?: string | undefined;
*  sizesMinMaxprice?: string | undefined;
*  sizesMaxMaxprice?: string | undefined;
* }} PriceAttributes
*/

/**
* @typedef {Map<{date: string; sizes: string[]}} SizeMap
*/

/**
* @typedef {{
*  id: string;
*  size: string | null;
*  date: string;
* }} ElementAttribute
*/

/**
* @typedef {{
*  id: string;
*  size: string | null;
*  date: string;
*  element: HTMLElement;
*  hasMultipleDates: boolean;
*  sizesMap: SizeMap | null;
* }} FormattedProduct
*/

/**
* @typedef {{
*  id: string;
*  sizes: Array<{
*    id: string;
*    price: {
*      "gross": OmnibusPrice;
*      "net": OmnibusPrice;
*    };
*  }>
* }} Product
*/

/**
* @typedef {Map<string, Product>} ProductMap
*/

/**
* @typedef {{
*  price: string | null,
*  omnibusMin: OmnibusPrice
*  omnibusMax: OmnibusPrice
* }} OmnibusPriceRecord
*/

/**
* @typedef {Record<any, {
*    validate: () => boolean;
*    updatePrice: (element?: HTMLElement, prices?: OmnibusPriceRecord, omnibusType: OmnibusType) => void;
*    handleAttributes: (element?: HTMLElement, omnibusType?: OmnibusType) => ElementAttribute | null;
*  }>} omnibusTypeHandlers
*/

let app_shop = {};

/**
* Implementacja fallbacku Omnibusa na bramkę GraphQL
*
* @class OmnibusGraphqlFallback
*/
class OmnibusGraphqlFallback {
 constructor(dateElementList) {
   if (Array.isArray(dateElementList) && dateElementList.length === 0) return;
   if (!dateElementList) return;

   /**
    * Lista elementów HTML z atrybutami zawierającymi datę ostatniej zmiany ceny
    * @type {HTMLElement[]} dateElementList
    * @public
    */
   this.dateElementList = dateElementList;

   /**
    * Kolekcja produktów zwróconych z bramki GraphQL
    * @type {ProductMap} product
    * @public
    */
   this.productsMap = new Map();

   /**
    * Tablica zawierająca listę sformatowanych produktów wraz z ich datami, rozmiarami oraz pozostałymi właściwościami
    * @type {Product[]} formattedProductList
    * @public
    */
   this.formattedProductList = [];

   /**
    * Parametr przechowujący typ aktualnej ceny (netto / brutto)
    * @type {"net" | "gross"} priceType
    * @readonly
    * @public
    */
   this.priceType = app_shop?.vars?.priceType || "gross";

   /**
    * Rodzaje komponentów, w których znajduje się Omnibus
    * @enum {OmnibusType}
    * @readonly
    */
   this.omnibusTypes = {
     sizes: "SIZES",
     collection: "COLLECTION",
     hotspot: "HOTSPOT",
     search: "SEARCH",
     "search-b2b-list": "SEARCH-B2B-LIST",
     details: "DETAILS",
     "details-b2b": "details-B2B",
   };

   /**
    * Obiekt przechowujący metody walidacji, aktualizacji ceny itp. dla poszczególnych komponentów,
    * w których Omnibus jest dodany
    * @type {omnibusTypeHandlers}
    * @readonly
    * @public
    */
   this.omnibusTypeHandlers = {
     [this.omnibusTypes["sizes"]]: {
       validate: () => false,
       updatePrice: (...args) => this.setSizeOmnibusPrice(...args),
       handleAttributes: (...args) => this.handleProjectorAttributes(...args),
     },

     [this.omnibusTypes["collection"]]: {
       validate: () => false,
       updatePrice: (...args) => this.setOmnibusPrice(...args),
       handleAttributes: (...args) => this.handleElementAttributes(...args),
     },

     [this.omnibusTypes["hotspot"]]: {
       validate: (...args) => this.validateSize(...args),
       updatePrice: (...args) => this.setOmnibusPrice(...args),
       handleAttributes: (...args) => this.handleElementAttributes(...args),
     },

     [this.omnibusTypes["search"]]: {
       validate: (...args) => this.validateSizes(...args),
       updatePrice: (...args) => this.setOmnibusPrice(...args),
       handleAttributes: (...args) => this.handleElementAttributes(...args),
     },

     [this.omnibusTypes["search-b2b-list"]]: {
       validate: (...args) => this.validateMaxprice(...args),
       updatePrice: (...args) => this.setOmnibusPrice(...args),
       handleAttributes: (...args) => this.handleElementAttributes(...args),
     },

     [this.omnibusTypes["details"]]: {
       validate: (...args) => this.validateSizes(...args),
       updatePrice: (...args) => this.setOmnibusPrice(...args),
       handleAttributes: (...args) => this.handleProjectorAttributes(...args),
     },

     [this.omnibusTypes["details-b2b"]]: {
       validate: (...args) => this.validateMaxprice(...args),
       updatePrice: (...args) => this.setOmnibusPrice(...args),
       handleAttributes: (...args) => this.handleProjectorAttributes(...args),
     },
   };

   // Inicjalizacja fallbacku funkcji wysyłajcej zapytanie graphql
   this.initAppShopGraphql();
   // Pobranie wszystkich produktów oraz wyświetlenie odpowiednich danych
   this.init();
 }

 /**
  * Waliduje ceny produktu - wymagane do wyliczenia skróconej wersji Omnibusa
  * Używane w hotspotach
  *
  * @param {OmnibusPriceRecord} prices
  * @param {PriceAttributes} attr
  * @param {OmnibusType} omnibusType
  * @return {boolean}
  * @memberof OmnibusGraphqlFallback
  */
 validateSize(prices, attr, omnibusType) {
   if (!prices || !attr || !omnibusType) return false;
   const { omnibusMin, omnibusMax } = prices;

   return (
     (attr?.sizeMin != attr?.sizeMax &&
       attr?.sizeMinMaxprice == omnibusMin?.value &&
       attr?.sizeMaxMaxprice == omnibusMax?.value) ||
     attr?.maxprice == omnibusMin?.value
   );
 }

 /**
  * Waliduje ceny produktu - wymagane do wyliczenia skróconej wersji Omnibusa
  * Używane w search, projector details
  *
  * @param {OmnibusPriceRecord} prices
  * @param {PriceAttributes} attr
  * @param {OmnibusType} omnibusType
  * @return {boolean}
  * @memberof OmnibusGraphqlFallback
  */
 validateSizes(prices, attr, omnibusType) {
   if (!prices || !attr || !omnibusType) return false;
   const { omnibusMin, omnibusMax } = prices;

   return (
     (attr?.sizesMinMaxprice != attr?.sizesMaxMaxprice &&
       attr?.sizesMinMaxprice == omnibusMin?.value &&
       attr?.sizesMaxMaxprice == omnibusMax?.value) ||
     attr?.maxprice == omnibusMin?.value
   );
 }

 /**
  * Waliduje ceny produktu - wymagane do wyliczenia skróconej wersji Omnibusa
  * Używane w szablonach b2b (search, projector details)
  *
  * @param {OmnibusPriceRecord} prices
  * @param {PriceAttributes} attr
  * @param {OmnibusType} omnibusType
  * @return {boolean}
  * @memberof OmnibusGraphqlFallback
  */
 validateMaxprice(prices, attr, omnibusType) {
   if (!prices || !attr || !omnibusType) return false;
   const { omnibusMin } = prices;

   return attr?.maxprice == omnibusMin?.value || attr?.sizesMinMaxprice == omnibusMin?.value;
 }

 /**
  * Zwraca rodzaj komponentu z Omnibusem
  *
  * @param {HTMLElement} element
  * @return {string | null}
  * @memberof OmnibusGraphqlFallback
  */
 getOmnibusType(element) {
   const omnibusType = element?.getAttribute("data-omnibus-type");

   if (omnibusType && omnibusType in this.omnibusTypes) {
     return this.omnibusTypes?.[omnibusType];
   }

   return null;
 }

 /**
  * Sprawdza czy ma zostać wyświetlona skrócona wersja Omnibusa
  *
  * @param {OmnibusPriceRecord} omnibusPrices
  * @param {PriceAttributes} attributes
  * @param {OmnibusType} omnibusType
  * @return {boolean}
  * @memberof OmnibusGraphqlFallback
  */
 isOmnibusShortEnabled(omnibusPrices, attributes, omnibusType) {
   if (!omnibusType || !omnibusPrices || !attributes) return false;

   return !!this.omnibusTypeHandlers[omnibusType]?.validate(omnibusPrices, attributes, omnibusType);
 }

 /**
  * Zwraca wybrane atrybuty wymagane do wyliczania Omnibus short
  *
  * @param {HTMLElement} element
  * @return {PriceAttributes}
  * @memberof OmnibusGraphqlFallback
  */
 getElementAttributes(element) {
   return {
     maxprice: element?.getAttribute("data-maxprice"),
     sizeMin: element?.getAttribute("data-size_min"),
     sizeMax: element?.getAttribute("data-size_max"),
     sizeMinMaxprice: element?.getAttribute("data-size_min_maxprice"),
     sizeMaxMaxprice: element?.getAttribute("data-size_max_maxprice"),
     sizesMinMaxprice: element?.getAttribute("data-sizes_min_maxprice"),
     sizesMaxMaxprice: element?.getAttribute("data-sizes_max_maxprice"),
   };
 }

 /**
  * Funkcja dodająca lub usuwająca klasy wskazane w tablicy
  *
  * @param {Object} param
  * @param {HTMLElement | HTMLElement[]} [param.element=null] Element lub lista elementów,
  * do których mają zostać dodane/usunięte wskazane klasy
  * @param {string[]} [param.classList=[]] Tablica z listą klas
  * @param {boolean} [param.add=true] Flaga ustalająca czy ma dodać wskazane klasy
  * @return {void}
  * @memberof OmnibusGraphqlFallback
  */
 toggleElementClass({ element = null, classList = [], add = true } = {}) {
   if (!element) return;

   const elements = Array.isArray(element) ? element : [element];

   classList?.forEach((item) => {
     elements?.forEach((element) => {
       if (add) {
         element?.classList?.add(item);
       } else {
         element?.classList?.remove(item);
       }
     });
   });
 }

 /**
  * Podmiana Omnibusa w htmlu
  *
  * @param {HTMLElement} element
  * @param {string} price
  * @return {void}
  * @memberof OmnibusGraphqlFallback
  */
 replaceOmnibusPrice(element, price) {
   if (!element) return;

   element.textContent = price;
 }

 /**
  * Dodaje produkt do kolekcji productsMap
  *
  * @param {string} id
  * @param {Product} data
  * @returns {void}
  * @memberof OmnibusGraphqlFallback
  */
 addProduct(id, data) {
   const product = this.productsMap.get(id);

   const mergedSizes = this.mergeReceivedSizes(product, data);
   const mergedData = {
     ...product,
     ...data,
     sizes: mergedSizes,
   };

   this.productsMap.set(id, mergedData);
 }

 /**
  * Zapisuje produkty z parametru productsMap w pamięci (SessionStorage)
  *
  * @memberof OmnibusGraphqlFallback
  */
 saveStorage() {
   try {
     const data = JSON.stringify([...this.productsMap]);
     sessionStorage.setItem("omnibus-fallback-products-list", data);
   } catch (error) {
     console.error("[OmnibusFallback]:saveStorage Error", error);
   }
 }

 /**
  * Pobiera zapisane produkty w sessionStorage i zapisuje je w atrybucie productsMap
  *
  * @return { void }
  * @memberof OmnibusGraphqlFallback
  */
 getStorage() {
   const products = sessionStorage.getItem("omnibus-fallback-products-list");
   if (!products) return;

   try {
     const parsed = JSON.parse(products);
     this.productsMap = new Map(parsed);
   } catch (error) {
     console.error("[OmnibusFallback]:getStorage Error", error);
   }
 }

 /**
  * Parsuje "tablicę" z datami i przypisanymi do nich rozmiarami a następnie zwraca utworzoną kolekcję
  *
  * @param {string[]} dates Daty zapisane w formacie "data;size"
  * @return {SizeMap | null}
  * @memberof OmnibusGraphqlFallback
  */
 transformMultipleDates(dates) {
   if (!dates) return null;
   const tempMap = new Map();

   dates?.forEach((dateElement) => {
     let [s_date, s_size] = dateElement?.split(";");
     s_date = !!s_date ? s_date.trim() : null;
     s_size = !!s_size ? s_size.trim().replace(/'/gim, "") : null;

     if (!s_date || !s_size) return;

     const mapElement = tempMap.get(s_date);
     const sizes = mapElement ? [...mapElement?.sizes, s_size] : [s_size];
     const obj = {
       date: s_date,
       sizes,
     };

     tempMap.set(s_date, obj);
   });

   return tempMap;
 }

 /**
  * Pobiera listę elementów HTML z datami oraz odpowiednimi atrybutami a następnie
  * modyfikuje zwrócone dane i zapisuje je w parametrze formattedProductList
  *
  * @memberof OmnibusGraphqlFallback
  */
 prepareProductList() {
   this.formattedProductList = [];

   [...this.dateElementList].map((element) => {
     const omnibusType = this.getOmnibusType(element);
     if (!omnibusType) return;

     const attributes = this.omnibusTypeHandlers[omnibusType]?.handleAttributes(element, omnibusType);
     if (!attributes) return;
     const { id, size, date } = attributes;

     const splitDates = date?.split("#separator#");
     const hasMultipleDates = splitDates?.length > 1;
     const sizesMap = hasMultipleDates ? this.transformMultipleDates(splitDates) : null;

     const obj = {
       id,
       size,
       date,
       element,
       hasMultipleDates,
       sizesMap,
     };

     this.formattedProductList.push(obj);
   });
 }

 /**
  * Zwraca ID oraz datę produktu jeśli są zawarte w atrybutach elementu
  *
  * @param {HTMLElement=} element
  * @param {OmnibusType=} omnibusType
  * @return {ElementAttribute | null}
  * @memberof OmnibusGraphqlFallback
  */
 handleProjectorAttributes(element, omnibusType) {
   if (!element || !omnibusType) return null;

   const closest = element?.closest("#projector_form");
   if (!closest) return null;

   const id = closest?.getAttribute("data-product_id");
   const date = element?.getAttribute("data-last_price_change_date");

   if (!id || !date) return null;

   return { id, date };
 }

 /**
  * Zwraca ID, datę oraz rozmiar produktu jeśli są zawarte w atrybutach elementu
  *
  * @param {HTMLElement=} element
  * @param {OmnibusType=} omnibusType
  * @return {Pick<ElementAttribute, "id" | "date"> | null}
  * @memberof OmnibusGraphqlFallback
  */
 handleElementAttributes(element, omnibusType) {
   if (!element || !omnibusType) return null;

   const closest = element?.closest(".product") || element?.closest(".search_list__product");
   if (!closest) return null;

   const id =
     element?.getAttribute("data-omnibus_product_id") ||
     closest?.getAttribute("data-product_id") ||
     closest?.querySelector(".product__icon")?.getAttribute("data-product-id");
   const size = element?.getAttribute("data-omnibus_product_size");
   const date = element?.getAttribute("data-last_price_change_date");

   if (!id || !date) return null;

   return { id, size, date };
 }

 /**
  * Łączy istniejące rozmiary z nowymi
  *
  * @param {Product=} prev
  * @param {Product=} curr
  * @return {Product['sizes']}
  * @memberof OmnibusGraphqlFallback
  */
 mergeReceivedSizes(prev, curr) {
   if (!prev?.sizes && !curr?.sizes) return null;
   if (!prev?.sizes && curr?.sizes) return curr?.sizes;

   const sizes = Array.isArray(prev?.sizes) ? prev?.sizes : [prev?.sizes];
   const newSizes = Array.isArray(curr?.sizes) ? curr?.sizes : [curr?.sizes];

   return [
     ...newSizes.flat(10),
     ...sizes.filter((size) => newSizes.filter((newSize) => newSize?.id == size?.id).length === 0).flat(10),
   ];
 }

 /**
  * Modyfikuje strukturę HTML wyświetlając wyliczone ceny oraz dodaje odpowiednie klasy
  *
  * @param {HTMLElement=} element
  * @param {OmnibusPriceRecord=} prices
  * @param {OmnibusType} omnibusType
  * @returns {void}
  * @memberof OmnibusGraphqlFallback
  */
 setOmnibusPrice(element, prices, omnibusType) {
   if (!element || !prices?.price) return;

   this.toggleElementClass({ element, classList: ["--omnibus"] });

   const attributes = this.getElementAttributes(element);
//    const isOmnibusShortEnabled = this.isOmnibusShortEnabled(prices, attributes, omnibusType);
   const isOmnibusShortEnabled = false;

   const shortElement = element?.querySelector(".omnibus_short");
   const labelElement = element?.querySelector(".omnibus_label");
   const priceElement = element?.querySelector(".omnibus_price");
   const priceValueElement = priceElement?.querySelector(".omnibus_price__value");

   if (isOmnibusShortEnabled && shortElement) {
     this.toggleElementClass({ element, classList: ["--omnibus-short"] });
     this.toggleElementClass({ element: shortElement, classList: ["--hidden", "--hide"], add: false });
   }

   if (!isOmnibusShortEnabled && priceElement && priceValueElement) {
     this.replaceOmnibusPrice(priceValueElement, prices.price);

     this.toggleElementClass({
       element: [priceElement, labelElement],
       classList: ["--hidden", "--hide"],
       add: false,
     });
   }
 }

 /**
  * Dodaje atrybut 'data-omnibus' z Omnibusem - używane w rozmiarach na projectorze
  *
  * @param {HTMLElement=} element
  * @param {OmnibusPriceRecord=} prices
  * @return {void}
  * @memberof OmnibusGraphqlFallback
  */
 setSizeOmnibusPrice(element, prices) {
   if (!element || !prices) return;

   element.setAttribute("data-omnibus", prices.price);
 }

 /**
  * Sortuje rozmiary po cenach
  *
  * @param {Pick<Product, 'sizes'>=} sizes
  * @return {Pick<Product, 'sizes'>=} Posortowana tablica z rozmiarami
  * @memberof OmnibusGraphqlFallback
  */
 sortSizes(sizes) {
   return sizes?.sort((sizeA, sizeB) => {
     const priceA = sizeA?.price?.[this.priceType]?.value;
     const priceB = sizeB?.price?.[this.priceType]?.value;

     return priceA - priceB;
   });
 }

 /**
  * Zwraca najniższą lub najwyższą cenę Omnibusa
  *
  * @param {Product=} product
  * @param {boolean} [last=false] Flaga ustawiająca czy ma zwrócić najwyższą cenę (ostatnią posortowaną)
  * @return {OmnibusPrice | null}
  * @memberof OmnibusGraphqlFallback
  */
 getSizePrice(product, last = false) {
   const len = product?.sizes?.length;
   if (!len) return null;

   const idx = last ? len - 1 : 0;

   return this.sortSizes(product?.sizes)?.[idx]?.price?.[this.priceType];
 }

 /**
  * Obsługuje wyliczanie Omnibusa i modyfikację struktury HTML
  *
  * @memberof OmnibusGraphqlFallback
  */
 handleOmnibusPrices() {
   this.formattedProductList.forEach((formattedProduct) => {
     const id = formattedProduct?.id;
     const product = this.productsMap.get(id);
     if (!product || !formattedProduct?.element) return;

     const omnibusType = this.getOmnibusType(formattedProduct.element);
     const prices = this.calculateOmnibusPrices(product);
     if (!omnibusType || !prices) return;

     this.omnibusTypeHandlers[omnibusType]?.updatePrice(formattedProduct.element, prices, omnibusType);
   });
 }

 /**
  * Oblicza wartości Omnibusa
  *
  * @param {FormattedProduct} product
  * @return {OmnibusPriceRecord}
  * @memberof OmnibusGraphqlFallback
  */
 calculateOmnibusPrices(product) {
   const omnibusMin = this.getSizePrice(product);
   const omnibusMax = this.getSizePrice(product, true);
   let price = omnibusMin?.formatted ? `${omnibusMin?.formatted}` : null;

   if (
     price !== null &&
     omnibusMin?.formatted &&
     omnibusMax?.formatted &&
     omnibusMin?.value &&
     omnibusMax?.value &&
     omnibusMin.value != omnibusMax.value
   ) {
     price += ` - ${omnibusMax?.formatted}`;
   }

   return {
     price,
     omnibusMin,
     omnibusMax,
   };
 }

 /**
  * Zwraca listę produktów w postaci aliasów graphql jako string
  *
  * @param {string[]} productList
  * @return {string}
  * @memberof OmnibusGraphqlFallback
  */
 transformAliasQueries(productList) {
   if (!productList || productList?.length === 0) return null;

   return productList
     ?.map((productData, idx) => {
       if (!productData) return null;

       const aliasName = `fallback${idx}`;
       const param = `lastLowestPricesInput: [${productData}]`;
       const graphqlAlias = this.createGraphqlAlias(aliasName, param);

       return graphqlAlias;
     })
     ?.filter((el) => el)
     ?.join("");
 }

 /**
  * Zwraca listę produktów w postaci {id, size, date}
  *
  * @return {string[]}
  * @memberof OmnibusGraphqlFallback
  */
 getFormattedProductList() {
   const productList = this.formattedProductList.filter((product) => !this.productsMap.has(product.id));

   const separate = productList
     .filter(({ hasMultipleDates }) => !hasMultipleDates)
     ?.flatMap((el) => this.createProductTemplate(el))
     .filter((lst) => lst);

   const multiple = productList
     .filter(({ hasMultipleDates }) => hasMultipleDates)
     ?.flatMap((el) => this.createProductSizeTemplate(el))
     .filter((lst) => lst);

   return [separate.join(", ")].concat(multiple).filter((lst) => lst);
 }

 /**
  * Zwraca nową listę z produktami w formacie graphqla
  *
  * @param {Object} param
  * @param {string=} [param.id="0"] ID produktu
  * @param {Map<string, FormattedProduct>=} [param.sizesMap=null] Kolekcja z produktami
  * @param {boolean} [param.hasMultipleDates=true] Flaga ustalająca czy produkt zawiera więcej niż jedna data z rozmiarem
  * @return {string[]}
  * @memberof OmnibusGraphqlFallback
  */
 createProductSizeTemplate({ id = "0", sizesMap = null, hasMultipleDates = false } = {}) {
   if (!hasMultipleDates || sizesMap?.size === 0 || id == "0") return null;

   const templateList = [];

   sizesMap?.forEach(({ date, sizes }) => {
     sizes?.forEach((size) => {
       templateList.push(this.createProductTemplate({ id, date, size }));
     });
   });

   return templateList.filter((lst) => lst);
 }

 /**
  * Zwraca sformatowany string w formacie graphqla z danymi produktu (id, size, date)
  *
  * @param {Object} param
  * @param {string=} [param.id="0"] ID produktu
  * @param {string=} [param.size=""] Rozmiar produktu
  * @param {string=} [param.date=""] Data ostatniej zmiany cenyt
  * @return {string | null}
  * @memberof OmnibusGraphqlFallback
  */
 createProductTemplate({ id = "0", size = "", date = "" } = {}) {
   let template = "";

   if (id != "0") {
     template += `productId: ${id}`;
   }
   if (!!size) {
     template += `${template !== "" ? ", " : ""}sizeId: "${size}"`;
   }
   if (!!date) {
     template += `${template !== "" ? ", " : ""}date: "${date}"`;
   }

   return template !== "" ? `{ ${template} }` : null;
 }

 /**
  * Obsługuje dodawanie zwróconych produktów z bramki GraphQL do kolekcji productsMap
  *
  * @param {*} productListObject
  * @memberof OmnibusGraphqlFallback
  */
 handleGraphqlProductList(productListObject) {
   const list = [...Object.values(productListObject).flat(10)];

   list?.forEach((product) => {
     const id = product?.id;
     // if (typeof id === "undefined" || id == null) return;
     if (!id) return;

     const parsedId = `${id}`;

     this.addProduct(parsedId, product);
   });

   this.saveStorage();
 }

 /**
  * Zwraca alias graphql w postaci stringa
  *
  * @param {string} aliasName
  * @param {string} lowestPriceInput
  * @return {string}
  * @memberof OmnibusGraphqlFallback
  */
 createGraphqlAlias(aliasName, lowestPriceInput) {
   return `
 ${aliasName}: getLastLowestProductPrices(${lowestPriceInput}) {
   id
   sizes {
     id
     price {
       ${app_shop?.vars?.priceType || "gross"} {
         value
         currency
         formatted
       }
     }
   }
 }
`;
 }

 /**
  * Wysyła zapytanie do bramki GraphQL z aliasami produktów
  *
  * @param {string} formattedProductList
  * @return {Record<string, Product>=}
  * @memberof OmnibusGraphqlFallback
  */
 async fetchProducts(formattedProductList) {
   try {
     const req = await app_shop.graphql.getLowestPriceFallback(formattedProductList);

     return req?.data;
   } catch (error) {
     console.error("[OmnibusFallback]:fetchProducts Error", error);
     return null;
   }
 }

 /**
  * Inicjalizuje pobieranie produktów z pamięci lub bramki jeśli nie ma ich w SessionStorage
  * Uruchamia transformaty dla pobranych danych
  *
  * @param {HTMLElement[]} dateElementList
  * @return {void}
  * @memberof OmnibusGraphqlFallback
  */
 async init(dateElementList) {
   try {
     this.dateElementList = dateElementList || this.dateElementList;

     // Pobranie z cache zapisanych produktów
     this.getStorage();
     // Przygotowanie listy elementów z przypisanymi do nich ID produktów
     this.prepareProductList();

     const formattedProductList = this.getFormattedProductList();

     if (formattedProductList.length > 0) {
       const transformedList = this.transformAliasQueries(formattedProductList);
       if (!transformedList) return;

       const receivedProducts = await this.fetchProducts(transformedList);
       if (!receivedProducts) return;

       this.handleGraphqlProductList(receivedProducts);
     }

     this.handleOmnibusPrices();
   } catch (error) {
     console.error("[OmnibusFallback]:init Error", error);
   }
 }

 /**
  * Inicjalizuje funkcję wysyłania zapytania do bramki GraphQL
  *
  * @memberof OmnibusGraphqlFallback
  */
 initAppShopGraphql() {
   if (!app_shop.graphql) app_shop.graphql = {};

   const getLowestPriceFallback = async (lowestPriceInput) => {
     const lowestPriceQuery = JSON.stringify({
       query: `query {
         ${lowestPriceInput}
       }`,
     });

     try {
       const url = app_shop?.urls?.graphql
         ? `${app_shop?.urls?.graphql}?getLastLowestProductPricesFallback`
         : "/graphql/v1/?getLastLowestProductPricesFallback";

       const response = await fetch(url, {
         method: "POST",
         headers: {
           "Content-Type": "application/json",
           Accept: "application/json",
         },
         body: lowestPriceQuery,
       });

       const dataJson = await response.json();
       return dataJson;
     } catch (error) {
       console.error("Fetch getLowestPriceFallback() Error:", error);
       return false;
     }
   };

   if (typeof app_shop.graphql.getLowestPriceFallback === "undefined") {
     app_shop.graphql.getLowestPriceFallback = getLowestPriceFallback;
   }
 }
}

function runOmnibusFallback() {
    try {
        const lastPriceChangeDataList = document.querySelectorAll(
            "[data-last_price_change_date]:not([data-omnibus]):not(.--omnibus):not(.--omnibus-short)"
        );
        if (lastPriceChangeDataList.length === 0) return;
    
        if (typeof app_shop.omnibusGrapqhlFallback !== "undefined") {
            app_shop.omnibusGrapqhlFallback?.init?.(lastPriceChangeDataList);
        } else {
            app_shop.omnibusGrapqhlFallback = new OmnibusGraphqlFallback(lastPriceChangeDataList);
        }
    } catch (err) {
        console.error("[OmnibusFallback]:runOmnibusFallback Error", err);
    }
}


export default runOmnibusFallback;
  
// app_shop.run(
//     function initOmnibusFallback() {
//       if (typeof runOmnibusFallback === "function") {
//         runOmnibusFallback();
//       }
// }, "all", "[data-last_price_change_date]", true);